import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Funnel, X } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { useTableSort } from "../lib/useTableSort";
import { SortHeader } from "../components/SortHeader";

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("es-ES"); } catch { return d; }
};
const fmtPrice = (n) => (typeof n === "number" ? n.toFixed(2) + " €" : "—");

export default function AdminInvoiceSearch() {
  const [suppliers, setSuppliers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    supplier_id: "",
    document_type: "",   // '', 'factura', 'albaran'
    date_from: "",
    date_to: "",
    paid: "",            // '', 'true', 'false'
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.supplier_id) params.supplier_id = filters.supplier_id;
      if (filters.document_type) params.document_type = filters.document_type;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.paid !== "") params.paid = filters.paid;
      const [invRes, supRes] = await Promise.all([
        api.get("/invoices", { params }),
        suppliers.length === 0 ? api.get("/suppliers") : Promise.resolve({ data: suppliers }),
      ]);
      setInvoices(invRes.data);
      if (suppliers.length === 0) setSuppliers(supRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters, suppliers.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePaid = async (inv) => {
    if (inv.supplier_payment_method === "domiciliat") return;
    if (inv.document_type !== "factura") return;
    try {
      await api.patch(`/invoices/${inv.id}/paid`, { paid: !inv.paid });
      // Optimistic
      setInvoices((prev) => prev.map((x) => x.id === inv.id ? { ...x, paid: !inv.paid } : x));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const clearFilters = () => setFilters({ supplier_id: "", document_type: "", date_from: "", date_to: "", paid: "" });

  const stats = useMemo(() => {
    let total = 0, paidCount = 0, unpaidCount = 0;
    for (const it of invoices) {
      total += Number(it.total) || 0;
      if (it.document_type === "factura") {
        if (it.paid) paidCount += 1;
        else unpaidCount += 1;
      }
    }
    return { total, paidCount, unpaidCount };
  }, [invoices]);

  const supplierName = (id) => suppliers.find((s) => s.id === id)?.name || "—";
  const supplierPm = (id) => suppliers.find((s) => s.id === id)?.payment_method || "transferencia";

  const { sortedRows: sortedInvoices, sortKey, sortDir, toggleSort } = useTableSort(invoices, "supplier_name", "asc");

  const facturasCount = invoices.filter((i) => i.document_type === "factura").length;
  const albaranesCount = invoices.filter((i) => i.document_type === "albaran").length;

  return (
    <div>
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="label-cap">proveedores</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Facturas</h1>
        </div>
        <div className="text-sm text-[var(--text-secondary)] flex items-center gap-4">
          <span data-testid="stats-count">{invoices.length} documento{invoices.length === 1 ? "" : "s"}</span>
          <span className="font-mono font-semibold">{fmtPrice(stats.total)}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="card-flat p-5 mb-6" data-testid="invoice-filters">
        <div className="flex items-center gap-2 mb-4">
          <Funnel size={18} weight="duotone" className="text-[var(--primary)]" />
          <div className="label-cap">filtros</div>
          <button
            onClick={clearFilters}
            data-testid="clear-filters"
            className="ml-auto inline-flex items-center gap-1 text-xs text-[var(--text-secondary)] hover:text-[var(--danger)]"
          >
            <X size={14} /> Limpiar
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="label-cap block mb-1.5">Proveedor</label>
            <select
              value={filters.supplier_id}
              onChange={(e) => setFilters((f) => ({ ...f, supplier_id: e.target.value }))}
              data-testid="filter-supplier"
              className="w-full px-3 py-2.5 rounded-xl border border-[var(--border)] bg-white text-sm focus:outline-none focus:border-[var(--primary)]"
            >
              <option value="">Todos</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-cap block mb-1.5">Tipo</label>
            <select
              value={filters.document_type}
              onChange={(e) => setFilters((f) => ({ ...f, document_type: e.target.value }))}
              data-testid="filter-doctype"
              className="w-full px-3 py-2.5 rounded-xl border border-[var(--border)] bg-white text-sm focus:outline-none focus:border-[var(--primary)]"
            >
              <option value="">Todos ({invoices.length})</option>
              <option value="factura">Facturas ({facturasCount})</option>
              <option value="albaran">Albaranes ({albaranesCount})</option>
            </select>
          </div>
          <div>
            <label className="label-cap block mb-1.5">Desde</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
              data-testid="filter-datefrom"
              className="w-full px-3 py-2.5 rounded-xl border border-[var(--border)] bg-white text-sm focus:outline-none focus:border-[var(--primary)]"
            />
          </div>
          <div>
            <label className="label-cap block mb-1.5">Hasta</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
              data-testid="filter-dateto"
              className="w-full px-3 py-2.5 rounded-xl border border-[var(--border)] bg-white text-sm focus:outline-none focus:border-[var(--primary)]"
            />
          </div>
          <div>
            <label className="label-cap block mb-1.5">Estado pago</label>
            <select
              value={filters.paid}
              onChange={(e) => setFilters((f) => ({ ...f, paid: e.target.value }))}
              disabled={filters.document_type === "albaran"}
              data-testid="filter-paid"
              className="w-full px-3 py-2.5 rounded-xl border border-[var(--border)] bg-white text-sm focus:outline-none focus:border-[var(--primary)] disabled:opacity-50"
            >
              <option value="">Todas</option>
              <option value="true">Pagadas</option>
              <option value="false">Sin pagar</option>
            </select>
          </div>
        </div>
        {filters.document_type !== "albaran" && (
          <div className="mt-3 text-xs text-[var(--text-secondary)] flex flex-wrap gap-4">
            <span>✅ Pagadas: <span className="font-mono font-semibold text-emerald-600">{stats.paidCount}</span></span>
            <span>⏳ Sin pagar: <span className="font-mono font-semibold text-[var(--danger)]">{stats.unpaidCount}</span></span>
          </div>
        )}
      </div>

      {/* Results table */}
      <div className="card-flat overflow-hidden" data-testid="invoice-search-table">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[var(--border)] bg-[var(--bg)]">
              <th className="text-left p-4"><SortHeader sortKey="document_type" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Tipo</SortHeader></th>
              <th className="text-left p-4"><SortHeader sortKey="invoice_number" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Nº</SortHeader></th>
              <th className="text-left p-4"><SortHeader sortKey="invoice_date" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Fecha</SortHeader></th>
              <th className="text-left p-4"><SortHeader sortKey="supplier_name" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Proveedor</SortHeader></th>
              <th className="text-right p-4 label-cap">Líneas</th>
              <th className="text-right p-4"><SortHeader align="right" sortKey="total" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Total</SortHeader></th>
              <th className="text-center p-4"><SortHeader align="center" sortKey="paid" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Estado</SortHeader></th>
              <th className="text-right p-4 label-cap">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="p-8 text-center text-[var(--text-secondary)]">Cargando…</td></tr>
            ) : sortedInvoices.length === 0 ? (
              <tr><td colSpan={8} className="p-8 text-center text-[var(--text-secondary)]" data-testid="empty-results">No se encontraron documentos con estos filtros.</td></tr>
            ) : sortedInvoices.map((inv) => {
              const isFactura = (inv.document_type || "factura") === "factura";
              const pm = inv.supplier_payment_method || supplierPm(inv.supplier_id);
              return (
                <tr key={inv.id} className="border-b border-[var(--border)] last:border-0 hover:bg-[var(--bg)]" data-testid={`invoice-row-${inv.id}`}>
                  <td className="p-4">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-display font-bold uppercase tracking-wider ${
                      isFactura ? "bg-[var(--primary)]/15 text-[var(--primary)]" : "bg-slate-500/15 text-slate-600"
                    }`}>
                      <FileText size={12} weight="duotone" />
                      {isFactura ? "Factura" : "Albarán"}
                    </span>
                  </td>
                  <td className="p-4 font-mono">{inv.invoice_number || "—"}</td>
                  <td className="p-4">{fmtDate(inv.invoice_date)}</td>
                  <td className="p-4">
                    <Link to={`/admin/suppliers/${inv.supplier_id}`} className="hover:text-[var(--primary)]" data-testid={`invoice-supplier-${inv.id}`}>
                      {inv.supplier_name || supplierName(inv.supplier_id)}
                    </Link>
                  </td>
                  <td className="p-4 text-right font-mono">{inv.lines?.length || 0}</td>
                  <td className="p-4 text-right font-mono font-semibold">{fmtPrice(inv.total)}</td>
                  <td className="p-4 text-center">
                    {!isFactura ? (
                      <span className="text-xs text-[var(--text-secondary)]">—</span>
                    ) : pm === "domiciliat" ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-lg bg-[var(--primary)]/15 text-[var(--primary)] text-xs font-display font-bold uppercase tracking-wider" data-testid={`inv-domiciliat-${inv.id}`}>
                        Domiciliado
                      </span>
                    ) : (
                      <button
                        onClick={() => togglePaid(inv)}
                        data-testid={`inv-toggle-paid-${inv.id}`}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-display font-bold uppercase tracking-wider transition ${
                          inv.paid
                            ? "bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25 border border-emerald-500/40"
                            : "bg-white text-[var(--text-secondary)] hover:bg-[var(--bg)] border border-[var(--border)]"
                        }`}
                      >
                        {inv.paid ? "✓ Pagada" : "Sin pagar"}
                      </button>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end">
                      <Link
                        to={`/admin/suppliers/${inv.supplier_id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--border)] hover:bg-white text-sm font-display font-semibold"
                        data-testid={`inv-view-${inv.id}`}
                      >
                        Ver
                      </Link>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
